// eslint-disable-next-line @eslint-community/eslint-comments/disable-enable-pair
/* eslint-disable camelcase */

'use client';

import classnames from 'classnames/bind';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { Fragment, useEffect, useRef, useState } from 'react';
import { Text } from '@portals/ui-kit';
import { watchOfIntersecting } from '@/_utils/watch-of-intersecting';
import {
  EAnalytics,
  ENumberOfCalls,
  WATCH_OF_INTERSECTING_DELAY,
} from '@/_constants/analytics';
import { useAnalytics } from '@/_hooks/use-analytics';
import { ECountries } from '@/_types';
import { ECardLinks, TCard } from '../../_types';
import { CardButton } from '../card-button';
import styles from './index.module.scss';

const SalesModelsDrawer = dynamic(
  () => import('@/_components/sales-models-drawer'),
  {
    ssr: false,
  },
);

const cn = classnames.bind(styles);
const BLOCK_NAME = 'Card';

type TProps = TCard & {
  country: ECountries;
};

export const Card = ({
  title,
  links,
  texts,
  list,
  index,
  drawer,
  country,
  id: cardId,
}: TProps) => {
  const intersectionTargetRef = useRef<HTMLDivElement>(null);
  const translations = useTranslations();
  const sendEvent = useAnalytics();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const canSeeLinks =
    (country === ECountries.RU ||
      country === ECountries.ZH ||
      country === ECountries.BY ||
      country === ECountries.KZ) &&
    links;

  useEffect(() => {
    if (intersectionTargetRef.current) {
      const handleAnalyticsEvent = ({
        duration,
      }: {
        duration: number;
      }): void => {
        const config = {
          name: EAnalytics.SUPPLIER_LANDING_STEPS_TO_START_V,
          params: {
            duration,
            block_number: Number(index),
          },
        };

        sendEvent(config);
      };

      return watchOfIntersecting({
        element: intersectionTargetRef.current,
        callback: handleAnalyticsEvent,
        delay: WATCH_OF_INTERSECTING_DELAY,
        numberOfCalls: ENumberOfCalls.REGULARLY,
      });
    }
  }, [index, sendEvent]);

  return (
    <div className={cn(BLOCK_NAME)}>
      <div
        className={cn(
          `${BLOCK_NAME}__paragraph`,
          `${BLOCK_NAME}__paragraph--index`,
        )}
      >
        <Text size="title-s" text={index} />
      </div>
      <div className={cn(`${BLOCK_NAME}__content`)}>
        <div className={cn(`${BLOCK_NAME}__title`)}>
          <Text size="title-m" text={translations(title)} />
        </div>
        <div className={cn(`${BLOCK_NAME}__info`)}>
          <div className={cn(`${BLOCK_NAME}__paragraphs`)}>
            {texts.map(({ name, id }) => (
              <div key={id} className={cn(`${BLOCK_NAME}__paragraph`)}>
                <Text size="body-l" text={translations(name)} />
              </div>
            ))}
          </div>
          {list?.length && (
            <ul className={cn(`${BLOCK_NAME}__list`)}>
              {list.map(({ name, id }) => (
                <li key={id} className={cn(`${BLOCK_NAME}__list-item`)}>
                  <Text size="body-s" text={translations(name)} />
                </li>
              ))}
            </ul>
          )}
        </div>
        {/*Кнопка дровера одна если нет ссылок в links*/}
        <div className={cn(`${BLOCK_NAME}__links`)}>
          {drawer && !links && (
            <Fragment>
              <div className={cn(`${BLOCK_NAME}__button`)}>
                <CardButton
                  name={drawer.buttonName}
                  onClick={() => {
                    const config = {
                      name: EAnalytics.SUPPLIER_LANDING_STEPS_TO_START_LINK_T,
                      params: {
                        block_number: index,
                        link_name: cardId,
                      },
                    };
                    sendEvent(config);
                    setIsDrawerOpen(true);
                  }}
                />
              </div>
              <SalesModelsDrawer
                {...drawer}
                isOpen={isDrawerOpen}
                onClose={handleDrawerClose}
              />
            </Fragment>
          )}

          {canSeeLinks &&
            links.map(({ name, route, id, isLink }) => {
              const isPromotionDetailsButton =
                id === ECardLinks.PROMOTION_DETAILS;
              const target = isPromotionDetailsButton ? '_blank' : '_self';

              const handleButtonClick = () => {
                const config = {
                  name: EAnalytics.SUPPLIER_LANDING_STEPS_TO_START_LINK_T,
                  params: {
                    block_number: index,
                    link_name: id,
                  },
                };
                sendEvent(config);

                window.open(route, target);
              };

              if (drawer) {
                const handleDrawerOpen = () => {
                  const config = {
                    name: EAnalytics.SUPPLIER_LANDING_STEPS_TO_START_LINK_T,
                    params: {
                      block_number: index,
                      link_name: id,
                    },
                  };
                  sendEvent(config);
                  setIsDrawerOpen(true);
                };

                return (
                  <Fragment key={id}>
                    <div className={cn(`${BLOCK_NAME}__button`)}>
                      <CardButton
                        name={drawer.buttonName}
                        onClick={handleDrawerOpen}
                      />
                    </div>

                    <div className={cn(`${BLOCK_NAME}__button--is-link`)}>
                      <CardButton
                        isLink
                        name={name}
                        onClick={handleButtonClick}
                      />
                    </div>

                    <SalesModelsDrawer
                      {...drawer}
                      isOpen={isDrawerOpen}
                      onClose={handleDrawerClose}
                    />
                  </Fragment>
                );
              }

              return (
                <div
                  key={id}
                  className={cn(`${BLOCK_NAME}__button`, {
                    [`${BLOCK_NAME}__button--is-link`]: isLink,
                  })}
                >
                  <CardButton
                    isLink={isLink}
                    name={name}
                    onClick={handleButtonClick}
                  />
                </div>
              );
            })}
        </div>
      </div>
      <div
        ref={intersectionTargetRef}
        className={cn(`${BLOCK_NAME}__observer`)}
      />
    </div>
  );
};
